(() => {
  const CART_EVENT = 'shopify:cart:lines-update';
  const EASE = 'cubic-bezier(.22,.61,.36,1)';
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  // Shopify Customer Privacy UI (banner + preferences dialog) injected outside the cart.
  const CONSENT_UI_SELECTOR = '#shopify-pc__banner, #shopify-pc__prefs, [id^="shopify-pc__"], [class*="shopify-pc__"]';
  const consentBanner = () => document.getElementById('shopify-pc__banner');
  // True when the pointer event started anywhere inside Shopify's privacy UI.
  // Walks the composed path (covers shadow DOM) and checks id/class prefixes.
  const isConsentInteraction = (event, path) => {
    if (event.target?.closest?.(CONSENT_UI_SELECTOR)) return true;
    return (path || []).some((node) => {
      if (!node || node.nodeType !== 1) return false;
      if (String(node.id || '').startsWith('shopify-pc__')) return true;
      const cls = typeof node.className === 'string' ? node.className : '';
      return cls.split(/\s+/).some((name) => name.startsWith('shopify-pc__'));
    });
  };
  // A 100g variant of a limited-lot coffee; preferred free-shipping filler.
  const LIMITED_100G = /(^|\b)100\s*g\b/i;
  // Prefer a 100g limited lot over the absolute cheapest gap-closer when it costs
  // at most this much more (kuruş).
  const LIMITED_PREFERENCE_TOLERANCE = 10000;
  // Grind choices (existing "Öğütüm" options) mapped to brew style.
  const ESPRESSO_GRINDS = /espresso|moka/i;
  const FILTER_GRINDS = /v60|chemex|aeropress|filtre|french|cold brew|origami|kalita/i;
  // Cross-sell scoring weights (see thresholdRecommendation()).
  const CROSS_SELL_WEIGHTS = {
    brewMatch: 3,
    brewMismatch: -2,
    sharedCollection: 1,
    limitedDiscovery: 1,
    closesGap: 3,
    extraSpendPer100: 0.5,
    limited100g: 1
  };

  const INVOICE_FIELDS = ['company', 'taxId', 'taxOffice', 'address', 'city', 'district', 'email', 'phone'];
  // Storefront field -> Wemond invoice API field. These values are sent ONLY
  // to the Wemond invoice API, never to Shopify.
  const INVOICE_API_FIELDS = {
    company: 'company_name',
    taxId: 'tax_id',
    taxOffice: 'tax_office',
    address: 'address',
    city: 'city',
    district: 'district',
    email: 'email',
    phone: 'phone'
  };
  const INVOICE_API_PATH = '/v1/corporate-invoice-drafts';
  const INVOICE_REF_PATTERN = /^inv_[A-Za-z0-9_-]{22}$/;
  const EDIT_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
  const LEGACY_INVOICE_STORAGE_PREFIX = 'wemond:corporate-invoice:';
  const INVOICE_SESSION_KEY = 'wemond:corporate-invoice:v2';

  class InvoiceApiError extends Error {
    constructor(status, code, fields = null) {
      super(code);
      this.name = 'InvoiceApiError';
      this.status = status;
      this.fields = fields;
    }
  }

  class WemondMiniCart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.closeButton = this.querySelector('[data-wmc-close]');
      this.titleNode = this.querySelector('.wmc__heading strong');
      this.scroll = this.querySelector('[data-wmc-scroll]');
      this.items = this.querySelector('[data-wmc-items]');
      this.empty = this.querySelector('[data-wmc-empty]');
      this.count = this.querySelector('[data-wmc-count]');
      this.shippingSection = this.querySelector('[data-wmc-shipping]');
      this.shippingText = this.querySelector('[data-wmc-shipping-text]');
      this.shippingAmount = this.querySelector('[data-wmc-shipping-amount]');
      this.shippingTrack = this.querySelector('[data-wmc-shipping-track]');
      this.subtotal = this.querySelector('[data-wmc-subtotal]');
      this.discountRow = this.querySelector('[data-wmc-discount-row]');
      this.discount = this.querySelector('[data-wmc-discount]');
      this.shippingFeeNode = this.querySelector('[data-wmc-shipping-fee]');
      this.total = this.querySelector('[data-wmc-total]');
      this.related = this.querySelector('[data-wmc-related]');
      this.locationBlock = this.querySelector('[data-wmc-location]');
      this.status = this.querySelector('[data-wmc-status]');
      this.discountToggle = this.querySelector('[data-wmc-discount-toggle]');
      this.discountPanel = this.querySelector('[data-wmc-discount-panel]');
      this.discountInput = this.querySelector('[data-wmc-discount-input]');
      this.discountRemoveButton = this.querySelector('[data-wmc-discount-remove]');
      this.discountApplyButton = this.querySelector('[data-wmc-discount-apply]');
      this.discountStatus = this.querySelector('[data-wmc-discount-status]');
      this.appliedDiscountCodes = [];
      this.corporateButton = this.querySelector('[data-wmc-corporate-invoice]');
      this.invoicePanel = this.querySelector('[data-wmc-invoice-panel]');
      this.invoiceForm = this.querySelector('[data-wmc-invoice-form]');
      this.invoiceFieldsWrap = this.querySelector('[data-wmc-invoice-fields]');
      this.invoiceRecoverNote = this.querySelector('[data-wmc-invoice-recover]');
      this.invoiceStatus = this.querySelector('[data-wmc-invoice-status]');
      this.invoiceSaveButton = this.querySelector('[data-wmc-invoice-save]');
      this.invoiceCancelButton = this.querySelector('[data-wmc-invoice-cancel]');
      this.invoiceCloseButton = this.querySelector('[data-wmc-invoice-close]');
      this.invoiceRemoveButton = this.querySelector('[data-wmc-invoice-remove]');
      this.invoiceInputs = {};
      this.invoiceErrorNodes = {};
      INVOICE_FIELDS.forEach((field) => {
        this.invoiceInputs[field] = this.querySelector(`[data-wmc-inv="${field}"]`);
        this.invoiceErrorNodes[field] = this.querySelector(`[data-wmc-inv-error="${field}"]`);
      });
      this.invoiceCorporateActive = false;
      this.invoiceBusy = false;
      this.threshold = Math.max(0, Number(this.dataset.freeShippingThreshold) || 95000);
      this.shippingFee = Math.max(0, Number(this.dataset.shippingFee) || 0);
      this.grindOptions = String(this.dataset.grindOptions || '').split('||').map((item) => item.trim()).filter(Boolean);
      this.catalog = this.readCatalog();
      this.busy = false;
      this.hideTimer = 0;
      this.announceTimer = 0;
      this.recommendationRequest = 0;
      this.recommendationPromise = Promise.resolve();
      this.suspendChildMotion = false;
      this.inappHint = this.querySelector('[data-wmc-inapp-hint]');
      this.syncInappHint();
      this.setupConsentAwareness();

      this.closeButton?.addEventListener('click', () => this.close());
      this.discountToggle?.addEventListener('click', () => this.toggleDiscountPanel());
      this.discountApplyButton?.addEventListener('click', () => this.applyDiscount());
      this.discountRemoveButton?.addEventListener('click', () => this.removeDiscount());
      this.discountInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') { event.preventDefault(); this.applyDiscount(); }
      });
      this.corporateButton?.addEventListener('click', () => this.openInvoicePanel());
      this.invoiceCloseButton?.addEventListener('click', () => this.closeInvoicePanel());
      this.invoiceCancelButton?.addEventListener('click', () => this.closeInvoicePanel());
      this.invoiceRemoveButton?.addEventListener('click', () => this.removeInvoice());
      this.invoiceForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.saveInvoice();
      });
      INVOICE_FIELDS.forEach((field) => {
        this.invoiceInputs[field]?.addEventListener('input', () => this.clearInvoiceFieldError(field));
      });
      this.purgeLegacyInvoiceSession();
      this.addEventListener('click', (event) => this.handleClick(event));

      // Close the mini cart when the user presses anywhere outside the panel.
      // pointerdown runs before the following click, so ordinary page links/buttons
      // still receive their normal click after the drawer has started closing.
      // Presses on Shopify's privacy banner / preferences dialog (and the cart
      // trigger) are NOT outside presses: answering consent must keep the cart open.
      // Only pointerdown (mouse, touch and pen) closes; no other event does.
      this._outsidePointerDown = (event) => {
        if (!this.classList.contains('is-open')) return;
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        if (path.includes(this) || this.contains(event.target)) return;
        if (event.target.closest?.('[data-wemond-mini-cart-trigger]')) return;
        if (isConsentInteraction(event, path)) return;
        this.close();
      };
      document.addEventListener('pointerdown', this._outsidePointerDown, true);

      this._escapeKeyDown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this._escapeKeyDown);
    }

    disconnectedCallback() {
      if (this._outsidePointerDown) document.removeEventListener('pointerdown', this._outsidePointerDown, true);
      if (this._escapeKeyDown) document.removeEventListener('keydown', this._escapeKeyDown);
      this._consentResize?.disconnect();
      this._consentMutation?.disconnect();
      this.stopConsentWatch();
      if (this._consentWindowResize) {
        window.removeEventListener('resize', this._consentWindowResize);
        window.visualViewport?.removeEventListener('resize', this._consentWindowResize);
        window.visualViewport?.removeEventListener('scroll', this._consentWindowResize);
      }
    }

    // Shopify's privacy banner is fixed to the viewport bottom (z-index above the
    // cart). Whenever it is visible AND horizontally overlaps the open cart, lift
    // the cart by the banner's height so the location selectors and the checkout
    // CTA stay visible and clickable. Consent mechanics are never touched.
    // The banner is re-queried on every check (Shopify may re-render it) and its
    // height is used instead of its top edge, so slide-in transforms, late
    // injection and mobile toolbar resizes cannot leave the offset at 0.
    setupConsentAwareness() {
      this._consentWindowResize = () => this.updateConsentOffset();
      window.addEventListener('resize', this._consentWindowResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this._consentWindowResize, { passive: true });
      window.visualViewport?.addEventListener('scroll', this._consentWindowResize, { passive: true });
      if (typeof ResizeObserver === 'function') {
        this._consentResize = new ResizeObserver(() => this.updateConsentOffset());
      }
      this._consentTransition = () => this.updateConsentOffset();
      const attach = () => {
        const banner = consentBanner();
        if (!banner || banner === this._consentBanner) return;
        this._consentBanner = banner;
        this._consentResize?.observe(banner);
        banner.addEventListener('transitionend', this._consentTransition);
        banner.addEventListener('animationend', this._consentTransition);
        this.updateConsentOffset();
      };
      attach();
      if (typeof MutationObserver !== 'function') return;
      // Kept alive (cheap: body childList only + a direct id lookup) so a banner
      // that is injected late or replaced is always picked up.
      this._consentMutation = new MutationObserver(() => {
        attach();
        if (this.classList.contains('is-open')) this.updateConsentOffset();
      });
      this._consentMutation.observe(document.body, { childList: true });
    }

    // While the cart is open, re-check for a few seconds (banner entrance
    // animations, late consent script). Stops by itself.
    startConsentWatch() {
      this.stopConsentWatch();
      let runs = 0;
      this._consentWatch = window.setInterval(() => {
        runs += 1;
        this.updateConsentOffset();
        if (runs >= 12 || !this.classList.contains('is-open')) this.stopConsentWatch();
      }, 250);
    }

    stopConsentWatch() {
      if (this._consentWatch) window.clearInterval(this._consentWatch);
      this._consentWatch = 0;
    }

    updateConsentOffset() {
      const banner = consentBanner();
      let offset = 0;
      if (banner && this.classList.contains('is-open')) {
        const style = getComputedStyle(banner);
        const box = banner.getBoundingClientRect();
        const bottomAnchored = style.position === 'fixed' && style.bottom !== 'auto';
        const visible = style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0 && box.height > 0 && !banner.hidden;
        if (visible && bottomAnchored) {
          const cart = this.getBoundingClientRect();
          const overlapsHorizontally = box.left < cart.right && box.right > cart.left;
          const bottom = Math.max(0, parseFloat(style.bottom) || 0);
          if (overlapsHorizontally) offset = Math.ceil(box.height + bottom);
        }
      }
      const value = `${Math.max(0, offset)}px`;
      if (this.style.getPropertyValue('--wmc-consent-offset') !== value) this.style.setProperty('--wmc-consent-offset', value);
      this.classList.toggle('has-consent-offset', offset > 0);
    }

    syncInappHint() {
      if (!this.inappHint) return;
      const enabled = this.dataset.inappHint === 'on';
      const inApp = /\b(Instagram|FBAN|FBAV|FB_IAB)\b/i.test(navigator.userAgent || '');
      this.inappHint.hidden = !(enabled && inApp);
    }

    readCatalog() {
      try {
        const parsed = JSON.parse(this.querySelector('[data-wmc-catalog]')?.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }

    money(cents) {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format((Number(cents) || 0) / 100);
    }

    moneyExact(cents) {
      return this.money(cents);
    }

    // "₺110" / "₺72,50" — drops ",00" for whole-lira amounts in short copy.
    moneyShort(cents) {
      const value = (Number(cents) || 0) / 100;
      const whole = Number.isInteger(value);
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2
      }).format(value);
    }

    escape(value = '') {
      return String(value).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
      }[char]));
    }

    imageUrl(image) {
      let source = image?.url || image?.src || image || '';
      if (!source) return '';
      try {
        const url = new URL(source, location.origin);
        url.searchParams.set('width', '480');
        source = url.toString();
      } catch (_) {}
      return source;
    }

    itemImage(item) {
      return this.imageUrl(item.featured_image?.url || item.featured_image || item.image || '');
    }

    // Returns `value` as a finite number, or null if it is missing/blank/NaN.
    // Distinguishes a genuine 0 from "field absent", which plain `??`/`||`
    // coalescing on `Number(x)` cannot (Number(null) and Number('') are both
    // 0, which would silently mask a missing field as a valid zero amount).
    numberOrNull(value) {
      if (value === null || value === undefined || value === '') return null;
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    }

    // Single source of truth for free-shipping eligibility. Must match the
    // production Shopify Function's business rule (verified at runtime via
    // real Function invocation logs): GROSS merchandise subtotal, summed
    // before any discount, not the discounted/payable total — the closest
    // available equivalent to the Function's `cart.lines[].cost.subtotalAmount`
    // sum. Never the compare-at price, never a post-discount field. Used
    // identically for the shipping progress bar, the shipping fee row, the
    // displayed total, and the related-recommendation visibility threshold so
    // all four can never disagree with each other.
    //
    // Fallback order (each tier only used if the previous one is genuinely
    // absent, not just falsy):
    //   1. cart.original_total_price — cart-level pre-discount sum.
    //   2. sum(item.original_line_price) — reconstructed from lines, for the
    //      rare case the cart-level field itself is missing.
    //   3. cart.items_subtotal_price — same family of field, kept as a last
    //      resort even though it may already reflect line-item discounts on
    //      some cart shapes, which is why it ranks below both prior tiers.
    //   4. cart.total_price — final fail-safe so the UI never renders NaN.
    shippingEligibilityTotal(cart) {
      const cartLevel = this.numberOrNull(cart.original_total_price);
      if (cartLevel !== null) return cartLevel;

      const items = Array.isArray(cart.items) ? cart.items : [];
      if (items.length) {
        let sum = 0;
        let allValid = true;
        for (const item of items) {
          const lineGross = this.numberOrNull(item.original_line_price);
          if (lineGross === null) { allValid = false; break; }
          sum += lineGross;
        }
        if (allValid) return sum;
      }

      const itemsSubtotal = this.numberOrNull(cart.items_subtotal_price);
      if (itemsSubtotal !== null) return itemsSubtotal;

      return this.numberOrNull(cart.total_price) || 0;
    }

    hydrateNativeDrawer(sectionHTML) {
      if (!sectionHTML) return false;
      try {
        const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const freshDrawer = doc.querySelector('theme-drawer#cart-drawer');
        const currentDrawer = document.querySelector('theme-drawer#cart-drawer');
        if (!freshDrawer || !currentDrawer) return false;
        const freshInner = freshDrawer.querySelector('[data-hydration-key="cart-drawer-inner"]');
        const currentInner = currentDrawer.querySelector('[data-hydration-key="cart-drawer-inner"]');
        if (freshInner && currentInner) currentInner.replaceWith(freshInner);
        const freshEmpty = freshDrawer.querySelector('#empty-cart-template');
        const currentEmpty = currentDrawer.querySelector('#empty-cart-template');
        if (freshEmpty && currentEmpty) currentEmpty.innerHTML = freshEmpty.innerHTML;
        return Boolean(freshInner && currentInner);
      } catch (_) {
        return false;
      }
    }

    async syncNativeDrawer() {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(root, location.origin);
      url.searchParams.set('section_id', 'cart-drawer-section');
      try {
        const response = await fetch(url.toString(), { headers: { Accept: 'text/html' }, cache: 'no-store' });
        if (response.ok) this.hydrateNativeDrawer(await response.text());
      } catch (_) {}
    }

    isExperience(value) {
      const text = `${value?.handle || ''} ${value?.title || value?.product_title || ''}`;
      return /(?:espresso|filtre)-deneyim-paketi/i.test(text) || /Deneyim Paketi/i.test(text);
    }

    cartIdentity(cart) {
      return {
        ids: new Set((cart.items || []).map((item) => Number(item.product_id)).filter(Boolean)),
        handles: new Set((cart.items || []).map((item) => String(item.handle || '')).filter(Boolean))
      };
    }

    grindOptionsMarkup() {
      return this.grindOptions.map((option) => `<option value="${this.escape(option)}">${this.escape(option)}</option>`).join('');
    }

    // Brew intent of the cart, from data customers already chose: the grind
    // property on each line ("Öğütüm") and the experience-package recipe type.
    // Returns 'espresso', 'filter' or '' (unknown / beans only / mixed).
    cartBrewIntent(cart) {
      let espresso = 0;
      let filter = 0;
      (cart.items || []).forEach((item) => {
        const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
        Object.entries(properties).forEach(([key, value]) => {
          const name = String(key);
          const text = String(value || '');
          if (name === '_wemond_blend_lab_type') {
            if (/espresso/i.test(text)) espresso += 1;
            else if (/filt/i.test(text)) filter += 1;
            return;
          }
          if (name.startsWith('_') || !/öğüt|ogut/i.test(name)) return;
          if (ESPRESSO_GRINDS.test(text)) espresso += 1;
          else if (FILTER_GRINDS.test(text)) filter += 1;
        });
      });
      if (espresso && !filter) return 'espresso';
      if (filter && !espresso) return 'filter';
      return '';
    }

    // Free-shipping filler with relevance (existing data only):
    //   mandatory: available variant, coffee product, not already in the cart,
    //              never an experience package (catalog is pre-filtered in Liquid);
    //   score:     brew-intent match from the cart's grind choice vs the product's
    //              Kahvemi Bul espresso / filtre collections, shared curated
    //              collections with the cart's products, closing the gap, least
    //              extra spend, and a small bonus for a 100g limited lot.
    // With no usable metadata it falls back to the plain threshold-price rule
    // (cheapest gap-closer, 100g limited lot preferred within tolerance).
    // Never auto-added.
    thresholdRecommendation(cart) {
      const gap = this.threshold - this.shippingEligibilityTotal(cart);
      if (gap <= 0) return null;
      const identity = this.cartIdentity(cart);
      const byId = new Map((this.catalog || []).map((product) => [Number(product?.id) || 0, product]));
      const cartGroups = new Set();
      let cartHasLimited = false;
      (cart.items || []).forEach((item) => {
        const product = byId.get(Number(item.product_id) || 0);
        (product?.groups || []).forEach((group) => {
          if (group === 'limited-lot') cartHasLimited = true;
          else cartGroups.add(group);
        });
      });
      const intent = this.cartBrewIntent(cart);
      const candidates = [];
      (this.catalog || []).forEach((product) => {
        const productId = Number(product?.id) || 0;
        const handle = String(product?.handle || '');
        if (!productId || identity.ids.has(productId) || identity.handles.has(handle) || this.isExperience(product)) return;
        const groups = Array.isArray(product.groups) ? product.groups : [];
        (Array.isArray(product.variants) ? product.variants : []).forEach((variant) => {
          const price = Number(variant?.price) || 0;
          if (!variant?.id || variant.available === false || price <= 0) return;
          const variantTitle = variant.title && variant.title !== 'Default Title' ? String(variant.title) : '';
          candidates.push({
            id: productId,
            handle,
            title: String(product.title || ''),
            variantTitle,
            url: product.url || `/products/${handle}`,
            image: this.imageUrl(product.image || ''),
            variantId: Number(variant.id),
            price,
            groups,
            limited100g: LIMITED_100G.test(variantTitle)
          });
        });
      });
      if (!candidates.length) return null;

      const relevanceAvailable = Boolean(intent) || cartGroups.size > 0 || cartHasLimited;
      if (!relevanceAvailable) {
        const closers = candidates.filter((item) => item.price >= gap).sort((a, b) => a.price - b.price);
        if (closers.length) {
          const cheapest = closers[0];
          const limited = closers.find((item) => item.limited100g);
          const product = limited && limited.price <= cheapest.price + LIMITED_PREFERENCE_TOLERANCE ? limited : cheapest;
          return { product, closesGap: true, reason: '', gap };
        }
        const closest = candidates.sort((a, b) => b.price - a.price)[0];
        return { product: closest, closesGap: false, reason: '', gap };
      }

      const scored = candidates.map((item) => {
        const espressoFit = item.groups.some((group) => group.startsWith('wemond-secim-espresso'));
        const filterFit = item.groups.some((group) => group.startsWith('wemond-secim-filtre'));
        const brewMatch = (intent === 'espresso' && espressoFit) || (intent === 'filter' && filterFit);
        const brewMismatch = Boolean(intent) && !brewMatch;
        const shared = item.groups.filter((group) => group !== 'limited-lot' && cartGroups.has(group)).length;
        const discovery = cartHasLimited && item.groups.includes('limited-lot');
        const closesGap = item.price >= gap;
        let score = 0;
        if (brewMatch) score += CROSS_SELL_WEIGHTS.brewMatch;
        if (brewMismatch) score += CROSS_SELL_WEIGHTS.brewMismatch;
        score += Math.min(shared, 2) * CROSS_SELL_WEIGHTS.sharedCollection;
        if (discovery) score += CROSS_SELL_WEIGHTS.limitedDiscovery;
        if (closesGap) {
          score += CROSS_SELL_WEIGHTS.closesGap;
          score -= ((item.price - gap) / 10000) * CROSS_SELL_WEIGHTS.extraSpendPer100;
          if (item.limited100g) score += CROSS_SELL_WEIGHTS.limited100g;
        } else {
          score += CROSS_SELL_WEIGHTS.closesGap * (item.price / gap) * 0.8;
        }
        let reason = '';
        if (brewMatch) reason = intent === 'espresso' ? 'Espresso demlemene uygun' : 'Filtre demlemene uygun';
        else if (!brewMismatch && (shared > 0 || discovery)) reason = 'Sepetine uygun tamamlayıcı';
        return { item, score, closesGap, reason };
      }).sort((a, b) => (b.score - a.score) || (a.item.price - b.item.price));

      const best = scored[0];
      return { product: best.item, closesGap: best.closesGap, reason: best.reason, gap };
    }

    renderRecommendation(product, { closesGap = false, reason = '', gap = 0 } = {}) {
      if (!this.related || !product) return this.concealNode(this.related);
      // Copy is only allowed to promise free shipping when it is mathematically true.
      const remaining = Math.max(0, gap - (Number(product.price) || 0));
      const kicker = reason || (closesGap
        ? `${this.moneyShort(gap)} kaldı — bu ürünle ücretsiz kargoya geç`
        : 'Ücretsiz kargoya yaklaştırır');
      const note = closesGap
        ? 'Bu ürünle ücretsiz kargo kazanırsın'
        : (reason ? `Ücretsiz kargoya yaklaştırır · sonra ${this.moneyShort(remaining)} kalır` : `Sonra ${this.moneyShort(remaining)} kalır`);
      const title = product.variantTitle ? `${product.title} · ${product.variantTitle}` : product.title;
      this.related.innerHTML = `<div class="wmc-related__item">
        <a class="wmc-related__image" href="${this.escape(product.url)}">${product.image ? `<img src="${this.escape(product.image)}" alt="" loading="lazy">` : ''}</a>
        <div class="wmc-related__copy">
          <span class="wmc-related__kicker">${this.escape(kicker)}</span>
          <strong>${this.escape(title)}</strong>
          <span class="wmc-related__price">${this.money(product.price)}</span>
          <span class="wmc-related__note">${this.escape(note)}</span>
          <div class="wmc-related__action">
            <label class="visually-hidden" for="wmc-grind-${this.escape(product.variantId)}">Öğütüm seçeneği</label>
            <select id="wmc-grind-${this.escape(product.variantId)}" data-wmc-related-grind aria-label="Öğütüm seçeneği" required>
              <option value="">Öğütüm seç</option>${this.grindOptionsMarkup()}
            </select>
            <button type="button" class="wmc-related__add" data-wmc-related-add data-variant="${this.escape(product.variantId)}">Sepete ekle</button>
          </div>
        </div>
      </div>`;
      this.related.hidden = false;
      this.revealNode(this.related);
    }

    async loadRecommendation(cart) {
      if (!this.related || !cart.item_count || this.shippingEligibilityTotal(cart) >= this.threshold) {
        this.concealNode(this.related);
        return;
      }
      const requestId = ++this.recommendationRequest;
      const recommendation = this.thresholdRecommendation(cart);
      if (requestId !== this.recommendationRequest) return;
      // No sensible filler -> the shipping message alone is shown.
      if (recommendation) this.renderRecommendation(recommendation.product, recommendation);
      else this.concealNode(this.related);
    }

    async addRelated(button) {
      if (this.busy || !button?.dataset.variant) return;
      const row = button.closest('.wmc-related__item');
      const grind = row?.querySelector('[data-wmc-related-grind]');
      if (!grind?.value) {
        grind?.classList.add('is-invalid');
        grind?.focus();
        if (this.status) this.status.textContent = 'Lütfen öğütüm şeklini seç.';
        return;
      }
      grind.classList.remove('is-invalid');
      this.busy = true;
      button.disabled = true;
      button.textContent = 'Ekleniyor…';
      if (this.status) this.status.textContent = 'Önerilen kahve sepete ekleniyor.';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variant), quantity: 1, properties: { 'Öğütüm': grind.value } }],
            sections: 'cart-drawer-section', sections_url: location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Ürün eklenemedi.');
        if (!this.hydrateNativeDrawer(data.sections?.['cart-drawer-section'])) await this.syncNativeDrawer();
        const cart = await this.fetchCart();
        this.render(cart);
        if (this.status) this.status.textContent = 'Önerilen kahve sepete eklendi.';
      } catch (error) {
        button.disabled = false;
        button.textContent = 'Tekrar dene';
        if (this.status) this.status.textContent = error?.message || 'Bir hata oluştu.';
      } finally {
        this.busy = false;
      }
    }

    revealNode(node) {
      if (!node || node.hidden || node.dataset.wmcRevealed === 'true') return;
      node.dataset.wmcRevealed = 'true';
      if (this.suspendChildMotion || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      node.animate(
        [{ opacity: 0, transform: 'translate3d(0,12px,0)' }, { opacity: 1, transform: 'translate3d(0,0,0)' }],
        { duration: 380, easing: EASE }
      );
    }

    concealNode(node) {
      if (!node) return;
      delete node.dataset.wmcRevealed;
      node.hidden = true;
    }

    itemMeta(item) {
      const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};

      // Experience package variants are intentionally coded with compact Shopify
      // option values (for example B+C+H) so the builder can resolve the correct
      // price variant. Those internal codes should never be customer-facing.
      // The builder already sends the selected coffees as line-item properties,
      // so use their real product names in the mini cart instead.
      if (this.isExperience(item)) {
        const coffees = Object.entries(properties)
          .filter(([key, value]) => value && /^\d+\.\s*Kahve$/i.test(String(key).trim()))
          .sort(([a], [b]) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0))
          .map(([, value]) => String(value).trim())
          .filter(Boolean);

        if (coffees.length) return coffees.join('\n');
      }

      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      let grind = '';
      const entry = Object.entries(properties).find(([key, value]) => (
        value && !String(key).startsWith('_') && /öğüt|ogut/i.test(String(key))
      ));
      if (entry) grind = `${entry[0]}: ${entry[1]}`;

      return [variant, grind].filter(Boolean).join(' · ');
    }

    renderItems(cart) {
      if (!this.items) return;
      this.items.innerHTML = (cart.items || []).map((item) => {
        const image = this.itemImage(item);
        const itemMeta = this.itemMeta(item);
        const itemMetaMarkup = this.isExperience(item)
          ? itemMeta.split('\n').filter(Boolean).map((coffee) => `<span>${this.escape(coffee)}</span>`).join('')
          : this.escape(itemMeta);
        return `<li class="wmc-item" data-wmc-line data-key="${this.escape(item.key)}">
          <a class="wmc-item__image" href="${this.escape(item.url || '#')}">${image ? `<img src="${this.escape(image)}" alt="" loading="lazy">` : ''}</a>
          <div class="wmc-item__body">
            <a class="wmc-item__title" href="${this.escape(item.url || '#')}">${this.escape(item.product_title || item.title || '')}</a>
            <span class="wmc-item__price">${this.money(item.final_line_price)}</span>
            <span class="wmc-item__variant${this.isExperience(item) ? ' wmc-item__variant--experience' : ''}">${itemMetaMarkup}</span>
            <div class="wmc-item__controls">
              <div class="wmc-qty" aria-label="${this.escape(item.product_title || 'Ürün')} adedi">
                <button type="button" aria-label="Adedi azalt" data-wmc-minus>−</button>
                <output>${Number(item.quantity) || 1}</output>
                <button type="button" aria-label="Adedi artır" data-wmc-plus>+</button>
              </div>
              <button type="button" class="wmc-item__remove" data-wmc-remove>Kaldır</button>
            </div>
          </div>
        </li>`;
      }).join('');
    }

    render(cart) {
      const itemCount = Number(cart.item_count) || 0;
      const eligibilityTotal = this.shippingEligibilityTotal(cart);
      const left = Math.max(this.threshold - eligibilityTotal, 0);
      const progress = this.threshold ? Math.min(100, (eligibilityTotal / this.threshold) * 100) : 100;
      const freeShippingEligible = eligibilityTotal >= this.threshold;
      if (this.count) this.count.textContent = itemCount ? `${itemCount} ürün` : 'Boş';
      const originalTotal = eligibilityTotal;
      const payableTotal = Number(cart.total_price) || 0;
      // Prefer Shopify's own merchandise-discount total (real cart field,
      // never touched by our client-side synthetic shipping fee) over the
      // computed gross-minus-payable difference; a real total_discount of 0
      // must be respected as 0, not treated as falsy and recomputed — a
      // fallback-tier gross source that drifts even slightly from
      // cart.total_price would otherwise render a phantom tiny discount.
      const nativeDiscount = this.numberOrNull(cart.total_discount);
      const discountTotal = Math.max(0, nativeDiscount !== null ? nativeDiscount : originalTotal - payableTotal);
      const shippingFeeAmount = itemCount > 0 ? (freeShippingEligible ? 0 : this.shippingFee) : 0;
      const displayTotal = payableTotal + shippingFeeAmount;
      if (this.subtotal) this.subtotal.textContent = this.moneyExact(originalTotal);
      if (this.discount) this.discount.textContent = `−${this.moneyExact(discountTotal)}`;
      if (this.discountRow) this.discountRow.hidden = discountTotal <= 0;
      if (this.shippingFeeNode) this.shippingFeeNode.textContent = itemCount === 0 ? this.moneyExact(0) : (freeShippingEligible ? 'Ücretsiz' : this.moneyExact(shippingFeeAmount));
      if (this.total) this.total.textContent = this.moneyExact(displayTotal);
      if (this.shippingSection) this.shippingSection.hidden = itemCount === 0;
      // Same pre-discount eligibility total as the production shipping rule.
      if (this.shippingText) {
        this.shippingText.textContent = left > 0
          ? `Kargo ${this.moneyShort(this.shippingFee)} · ${this.moneyShort(left)} daha ekle, kargo ücretsiz`
          : 'Ücretsiz kargo kazandın';
      }
      if (this.shippingAmount) {
        this.shippingAmount.textContent = '';
        this.shippingAmount.hidden = true;
      }
      if (this.shippingTrack) this.shippingTrack.style.setProperty('--wmc-progress', `${progress}%`);
      if (this.empty) this.empty.hidden = itemCount > 0;
      if (this.items) this.items.hidden = itemCount === 0;
      if (this.locationBlock) this.locationBlock.hidden = itemCount === 0;
      this.syncDiscountUI(cart);
      this.syncInvoiceUI(cart);
      try { window.WemondRozet?.renderCart?.(cart, this); } catch (_) {}
      this.renderItems(cart);
      if (itemCount > 0 && !freeShippingEligible) {
        this.recommendationPromise = this.loadRecommendation(cart);
      } else {
        this.recommendationRequest += 1;
        this.concealNode(this.related);
        this.recommendationPromise = Promise.resolve();
      }
      this.updateCartBubble(itemCount);
    }

    async updateCartBubble(count) {
      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        try { await cartIcon.renderCartBubble(count); } catch (_) {}
      }
    }

    async fetchCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Sepet güncellenemedi.');
      return response.json();
    }

    async refresh({ preserveScroll = false } = {}) {
      const previousScroll = preserveScroll && this.scroll ? this.scroll.scrollTop : 0;
      const cart = await this.fetchCart();
      this.render(cart);
      if (preserveScroll && this.scroll) this.scroll.scrollTop = previousScroll;
      return cart;
    }

    async open({ announceAdd = false } = {}) {
      window.clearTimeout(this.hideTimer);
      window.clearTimeout(this.announceTimer);
      try { document.querySelector('theme-drawer#cart-drawer')?.close?.(); } catch (_) {}
      try {
        const cart = await this.fetchCart();
        this.render(cart);
        await this.recommendationPromise.catch(() => {});
      } catch (_) {}
      this.removeAttribute('hidden');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        this.updateConsentOffset();
        this.startConsentWatch();
        document.querySelectorAll('[data-wemond-mini-cart-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
        if (this.titleNode) {
          this.titleNode.textContent = announceAdd ? 'Sepete eklendi ✓' : 'Sepet';
          if (announceAdd) {
            this.announceTimer = window.setTimeout(() => {
              if (this.titleNode) this.titleNode.textContent = 'Sepet';
            }, 1800);
          }
        }
      }));
    }

    close() {
      this.stopConsentWatch();
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.querySelectorAll('[data-wemond-mini-cart-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        if (!this.classList.contains('is-open')) this.setAttribute('hidden', '');
      }, 600);
    }

    async change(key, quantity, row) {
      if (this.busy || !key) return;
      this.busy = true;
      const targetQuantity = Math.max(0, Number(quantity) || 0);
      row?.classList.add('is-busy');
      if (this.status) this.status.textContent = 'Sepet güncelleniyor.';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: targetQuantity, sections: 'cart-drawer-section', sections_url: location.pathname })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || 'Sepet güncellenemedi.');
        if (!this.hydrateNativeDrawer(cart.sections?.['cart-drawer-section'])) await this.syncNativeDrawer();
        if (targetQuantity === 0 && row && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          row.classList.add('is-removing');
          await wait(300);
        }
        const previousScroll = this.scroll?.scrollTop || 0;
        this.render(cart);
        if (this.scroll) this.scroll.scrollTop = previousScroll;
        if (this.status) this.status.textContent = 'Sepet güncellendi.';
      } catch (error) {
        row?.classList.remove('is-busy');
        if (this.status) this.status.textContent = error?.message || 'Bir hata oluştu.';
      } finally {
        this.busy = false;
      }
    }

    toggleDiscountPanel(force) {
      if (!this.discountPanel || !this.discountToggle) return;
      const shouldOpen = typeof force === 'boolean' ? force : this.discountPanel.hidden;
      this.discountPanel.hidden = !shouldOpen;
      this.discountToggle.setAttribute('aria-expanded', String(shouldOpen));
      if (shouldOpen) {
        this.closeInvoicePanel();
        this.discountInput?.focus();
      }
    }

    // Real, currently-applied customer discount CODES only — never an
    // automatic/code-less discount (e.g. the gross free-shipping Function,
    // which is a delivery discount and never appears in this list at all).
    // Confirmed at runtime: /cart.js's cart_level_discount_applications
    // entries carry `type: "discount_code"` and a flat `title` that equals
    // the literal code the customer entered (not the discount's Admin
    // display name) only for that type — other types (e.g. "automatic")
    // must never be surfaced in the discount input. De-duplicated,
    // case-insensitively.
    getAppliedDiscountCodes(cart) {
      const applications = Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [];
      const codes = [];
      const seen = new Set();
      for (const entry of applications) {
        if (entry?.type !== 'discount_code') continue;
        const code = String(entry.title || '').trim();
        if (!code) continue;
        const key = code.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        codes.push(code);
      }
      return codes;
    }

    // Keeps the discount input/remove-affordance in sync with the
    // authoritative cart. Skips overwriting the input value while the user
    // has it focused (actively typing a replacement) so an unrelated render
    // — a quantity change elsewhere, a background refresh — never stomps an
    // in-progress edit. applyDiscount()/removeDiscount() are the user's own
    // direct action on this field, so they force the authoritative value
    // afterward regardless of focus.
    syncDiscountUI(cart) {
      const codes = this.getAppliedDiscountCodes(cart);
      this.appliedDiscountCodes = codes;
      const primary = codes[0] || '';
      if (this.discountInput && document.activeElement !== this.discountInput) {
        this.discountInput.value = primary;
      }
      if (this.discountRemoveButton) this.discountRemoveButton.hidden = !primary;
    }

    async applyDiscount() {
      const code = (this.discountInput?.value || '').trim();
      if (!code || this.busy) return;
      this.busy = true;
      if (this.discountApplyButton) this.discountApplyButton.disabled = true;
      if (this.discountStatus) this.discountStatus.textContent = 'Uygulanıyor…';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code })
        });
        const updateResult = await response.json();
        if (!response.ok) throw new Error(updateResult.description || 'İndirim uygulanamadı.');
        // /cart/update.js's own response body can transiently carry an inflated or
        // inconsistent total right after an invalid discount code (a real Shopify
        // API quirk, confirmed at runtime). Always re-fetch and render the
        // authoritative /cart.js state instead of trusting this response for totals.
        const cart = await this.fetchCart();
        const codes = this.getAppliedDiscountCodes(cart);
        const applied = codes.some((entry) => entry.toLowerCase() === code.toLowerCase());
        this.render(cart);
        // Force-reconcile regardless of focus: this is the direct result of the
        // user's own submit action on this exact field, not an incidental
        // background render, so the authoritative result must always win —
        // whether that means the new code, the still-active previous code
        // (a rejected replacement), or empty (nothing active).
        if (this.discountInput) this.discountInput.value = codes[0] || '';
        if (this.discountRemoveButton) this.discountRemoveButton.hidden = !codes[0];
        if (this.discountStatus) {
          this.discountStatus.textContent = applied
            ? 'İndirim kodu uygulandı.'
            : 'Bu kod geçersiz veya sepetine uygulanamıyor.';
        }
      } catch (error) {
        if (this.discountStatus) this.discountStatus.textContent = error?.message || 'İndirim uygulanamadı.';
      } finally {
        this.busy = false;
        if (this.discountApplyButton) this.discountApplyButton.disabled = false;
      }
    }

    async removeDiscount() {
      if (this.busy) return;
      this.busy = true;
      if (this.discountRemoveButton) this.discountRemoveButton.disabled = true;
      if (this.discountApplyButton) this.discountApplyButton.disabled = true;
      if (this.discountStatus) this.discountStatus.textContent = 'Kaldırılıyor…';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' })
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.description || 'İndirim kaldırılamadı.');
        }
        // Same authoritative-refetch rule as applyDiscount(): trust /cart.js,
        // not the /cart/update.js response body, for the post-removal state.
        const cart = await this.fetchCart();
        this.render(cart);
        if (this.discountInput) this.discountInput.value = '';
        if (this.discountRemoveButton) this.discountRemoveButton.hidden = true;
        if (this.discountStatus) this.discountStatus.textContent = 'İndirim kodu kaldırıldı.';
      } catch (error) {
        if (this.discountStatus) this.discountStatus.textContent = error?.message || 'İndirim kaldırılamadı.';
      } finally {
        this.busy = false;
        if (this.discountRemoveButton) this.discountRemoveButton.disabled = false;
        if (this.discountApplyButton) this.discountApplyButton.disabled = false;
      }
    }

    // ---------------------------------------------------------------------
    // Corporate Invoice V2
    //
    // Raw invoice data goes ONLY to the Wemond invoice API over HTTPS. The
    // Shopify cart receives exactly two non-sensitive attributes:
    //   invoice_type = corporate
    //   invoice_ref  = inv_<opaque random>
    // sessionStorage holds only { invoiceRef, editToken } for same-tab
    // re-edit. Never localStorage, never raw field values, never in URLs.
    // ---------------------------------------------------------------------

    invoiceApiBase() {
      const base = String(this.invoicePanel?.dataset.wmcInvoiceApi || '').trim().replace(/\/+$/, '');
      return /^https:\/\/[a-z0-9.-]+(:\d+)?$/i.test(base) ? base : '';
    }

    readInvoiceSession() {
      try {
        const raw = sessionStorage.getItem(INVOICE_SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          INVOICE_REF_PATTERN.test(String(parsed.invoiceRef || '')) &&
          EDIT_TOKEN_PATTERN.test(String(parsed.editToken || ''))
        ) {
          return { invoiceRef: parsed.invoiceRef, editToken: parsed.editToken };
        }
      } catch (_) {}
      this.clearInvoiceSession();
      return null;
    }

    writeInvoiceSession(invoiceRef, editToken) {
      try {
        sessionStorage.setItem(INVOICE_SESSION_KEY, JSON.stringify({ invoiceRef, editToken }));
      } catch (_) {}
    }

    clearInvoiceSession() {
      try {
        sessionStorage.removeItem(INVOICE_SESSION_KEY);
      } catch (_) {}
    }

    // V1 QA builds stored raw invoice values under this prefix. Remove any
    // such entries so no raw PII lingers in the browser session.
    purgeLegacyInvoiceSession() {
      try {
        const legacyKeys = [];
        for (let index = 0; index < sessionStorage.length; index += 1) {
          const key = sessionStorage.key(index);
          if (key && key.startsWith(LEGACY_INVOICE_STORAGE_PREFIX) && key !== INVOICE_SESSION_KEY) legacyKeys.push(key);
        }
        legacyKeys.forEach((key) => sessionStorage.removeItem(key));
      } catch (_) {}
    }

    cartInvoiceRef(cart) {
      if (cart?.attributes?.invoice_type !== 'corporate') return null;
      const ref = String(cart?.attributes?.invoice_ref || '');
      return INVOICE_REF_PATTERN.test(ref) ? ref : null;
    }

    async invoiceApi(method, path = '', { editToken = null, body = null } = {}) {
      const base = this.invoiceApiBase();
      if (!base) throw new InvoiceApiError(0, 'NOT_CONFIGURED');
      const headers = { Accept: 'application/json' };
      if (body) headers['Content-Type'] = 'application/json';
      if (editToken) headers.Authorization = `Bearer ${editToken}`;
      let response;
      try {
        response = await fetch(`${base}${INVOICE_API_PATH}${path}`, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          referrerPolicy: 'no-referrer'
        });
      } catch (_) {
        throw new InvoiceApiError(0, 'NETWORK');
      }
      const json = response.status === 204 ? {} : await response.json().catch(() => ({}));
      if (!response.ok) throw new InvoiceApiError(response.status, json?.error || 'ERROR', json?.fields || null);
      return json;
    }

    async setCartInvoiceAttributes(attributes) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes })
      });
      if (!response.ok) throw new Error('Sepet güncellenemedi.');
      // Authoritative state comes from /cart.js, not the update response.
      return this.fetchCart();
    }

    // Best-effort draft cancellation; never surfaces errors to the customer.
    async cancelInvoiceDraft(invoiceRef, editToken) {
      if (!invoiceRef || !editToken) return;
      try {
        await this.invoiceApi('DELETE', `/${invoiceRef}`, { editToken });
      } catch (_) {}
    }

    toInvoiceApiBody(values) {
      const body = {};
      INVOICE_FIELDS.forEach((field) => {
        body[INVOICE_API_FIELDS[field]] = values[field];
      });
      return body;
    }

    invoiceErrorMessage(error) {
      if (error instanceof InvoiceApiError) {
        if (error.status === 429) return 'Çok fazla deneme yapıldı. Lütfen biraz sonra tekrar dene.';
        if (error.status === 400) return 'Lütfen eksik veya hatalı alanları düzelt.';
      }
      return 'Fatura bilgileri şu anda kaydedilemedi. Lütfen biraz sonra tekrar dene.';
    }

    showServerFieldErrors(fields) {
      if (!fields || typeof fields !== 'object') return;
      const errors = {};
      INVOICE_FIELDS.forEach((field) => {
        const code = fields[INVOICE_API_FIELDS[field]];
        if (!code) return;
        errors[field] = code === 'REQUIRED' ? 'Bu alan gerekli.' : 'Lütfen bu alanı kontrol et.';
      });
      this.showInvoiceErrors(errors);
    }

    normalizeTaxId(value) {
      return String(value || '').normalize('NFKC').trim().replace(/[\s.\-/_]/g, '');
    }

    validateTaxId(value) {
      const normalized = this.normalizeTaxId(value);
      if (!/^\d{10}$/.test(normalized) && !/^\d{11}$/.test(normalized)) return { valid: false, normalized };
      // All-same-digit values (incl. NetteFatura dummy identities
      // 1111111111 / 11111111111) are never a real VKN/TCKN.
      if (/^(\d)\1+$/.test(normalized)) return { valid: false, normalized };
      return { valid: true, normalized };
    }

    // Turkish numbers only; the server is authoritative and normalizes to +90.
    validatePhone(value) {
      let digits = String(value || '').normalize('NFKC').trim().replace(/[\s().\-/]/g, '').replace(/^\+/, '');
      if (!/^\d+$/.test(digits)) return { valid: false, normalized: '' };
      if (digits.length === 12 && digits.startsWith('90')) digits = digits.slice(2);
      else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
      if (!/^[2-5]\d{9}$/.test(digits)) return { valid: false, normalized: '' };
      return { valid: true, normalized: `+90${digits}` };
    }

    validateInvoiceForm() {
      const errors = {};
      const values = {};
      INVOICE_FIELDS.forEach((field) => {
        values[field] = String(this.invoiceInputs[field]?.value || '').replace(/\s+/g, ' ').trim();
      });

      if (!values.company) errors.company = 'Firma / ad soyad gerekli.';
      if (!values.taxOffice) errors.taxOffice = 'Vergi dairesi gerekli.';
      if (!values.address) errors.address = 'Fatura adresi gerekli.';
      if (!values.city) errors.city = 'İl gerekli.';
      if (!values.district) errors.district = 'İlçe gerekli.';

      if (!values.taxId) {
        errors.taxId = 'VKN veya TCKN gerekli.';
      } else {
        const taxIdCheck = this.validateTaxId(values.taxId);
        if (!taxIdCheck.valid) errors.taxId = 'VKN 10, TCKN 11 haneli geçerli bir numara olmalı.';
        else values.taxId = taxIdCheck.normalized;
      }

      if (!values.email) {
        errors.email = 'E-posta gerekli.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email)) {
        errors.email = 'Geçerli bir e-posta adresi gir.';
      }

      if (!values.phone) {
        errors.phone = 'Telefon gerekli.';
      } else {
        const phoneCheck = this.validatePhone(values.phone);
        if (!phoneCheck.valid) errors.phone = 'Geçerli bir telefon numarası gir.';
        else values.phone = phoneCheck.normalized;
      }

      return { valid: Object.keys(errors).length === 0, values, errors };
    }

    showInvoiceErrors(errors) {
      INVOICE_FIELDS.forEach((field) => {
        const message = errors[field] || '';
        if (this.invoiceErrorNodes[field]) this.invoiceErrorNodes[field].textContent = message;
        const wrapper = this.invoiceInputs[field]?.closest('.wmc__invoice-field');
        if (wrapper) wrapper.classList.toggle('is-invalid', Boolean(message));
      });
    }

    clearInvoiceFieldError(field) {
      if (this.invoiceErrorNodes[field]) this.invoiceErrorNodes[field].textContent = '';
      const wrapper = this.invoiceInputs[field]?.closest('.wmc__invoice-field');
      if (wrapper) wrapper.classList.remove('is-invalid');
    }

    resetInvoiceForm() {
      INVOICE_FIELDS.forEach((field) => {
        if (this.invoiceInputs[field]) this.invoiceInputs[field].value = '';
      });
      this.showInvoiceErrors({});
    }

    updateCorporateButtonState() {
      if (!this.corporateButton) return;
      this.corporateButton.textContent = this.invoiceCorporateActive ? 'Kurumsal fatura ✓' : 'Kurumsal fatura';
    }

    // Called from render() on every authoritative /cart.js fetch. Only the two
    // non-sensitive markers are read.
    syncInvoiceUI(cart) {
      const corporate = Boolean(this.cartInvoiceRef(cart));
      this.invoiceCorporateActive = corporate;
      this.updateCorporateButtonState();
      if (this.invoiceRemoveButton && this.invoicePanel && !this.invoicePanel.hidden) {
        this.invoiceRemoveButton.hidden = !corporate;
      }
    }

    async openInvoicePanel() {
      if (!this.invoicePanel) return;
      this.invoicePanel.hidden = false;
      this.classList.add('is-invoice-view');
      if (this.discountPanel) this.discountPanel.hidden = true;
      if (this.discountToggle) this.discountToggle.setAttribute('aria-expanded', 'false');
      this.corporateButton?.setAttribute('aria-expanded', 'true');
      if (this.invoiceStatus) this.invoiceStatus.textContent = '';
      await this.hydrateInvoicePanel();
      this.invoiceInputs.company?.focus();
    }

    closeInvoicePanel() {
      if (!this.invoicePanel || this.invoicePanel.hidden) return;
      this.invoicePanel.hidden = true;
      this.classList.remove('is-invoice-view');
      this.corporateButton?.setAttribute('aria-expanded', 'false');
      if (this.invoiceStatus) this.invoiceStatus.textContent = '';
    }

    // Prefill is only possible in the same tab/session that holds the edit
    // token. invoice_ref alone never retrieves anything.
    async hydrateInvoicePanel() {
      this.purgeLegacyInvoiceSession();
      let cart = null;
      try { cart = await this.fetchCart(); } catch (_) {}
      const cartRef = this.cartInvoiceRef(cart);
      this.invoiceCorporateActive = Boolean(cartRef);
      this.updateCorporateButtonState();
      if (this.invoiceRemoveButton) this.invoiceRemoveButton.hidden = !cartRef;
      this.resetInvoiceForm();
      if (this.invoiceRecoverNote) this.invoiceRecoverNote.hidden = true;

      const session = this.readInvoiceSession();

      if (!cartRef) {
        // Cart no longer references a draft: the stored token is useless.
        if (session) this.clearInvoiceSession();
        return;
      }

      if (!session || session.invoiceRef !== cartRef) {
        if (this.invoiceRecoverNote) this.invoiceRecoverNote.hidden = false;
        return;
      }

      try {
        const draft = await this.invoiceApi('GET', `/${cartRef}`, { editToken: session.editToken });
        const fields = draft?.fields || {};
        INVOICE_FIELDS.forEach((field) => {
          if (this.invoiceInputs[field]) this.invoiceInputs[field].value = String(fields[INVOICE_API_FIELDS[field]] || '');
        });
        if (this.invoiceInputs.phone && typeof fields.phone === 'string' && fields.phone.startsWith('+90')) {
          this.invoiceInputs.phone.value = `0${fields.phone.slice(3)}`;
        }
      } catch (error) {
        if (error instanceof InvoiceApiError && error.status === 404) this.clearInvoiceSession();
        this.resetInvoiceForm();
        if (this.invoiceRecoverNote) this.invoiceRecoverNote.hidden = false;
      }
    }

    async saveInvoice() {
      if (this.invoiceBusy) return;
      const { valid, values, errors } = this.validateInvoiceForm();
      if (!valid) {
        this.showInvoiceErrors(errors);
        if (this.invoiceStatus) this.invoiceStatus.textContent = 'Lütfen eksik veya hatalı alanları düzelt.';
        return;
      }
      this.showInvoiceErrors({});
      this.invoiceBusy = true;
      if (this.invoiceSaveButton) this.invoiceSaveButton.disabled = true;
      if (this.invoiceStatus) this.invoiceStatus.textContent = 'Kaydediliyor…';

      let createdRef = null;
      let createdToken = null;

      try {
        const cart = await this.fetchCart();
        const cartRef = this.cartInvoiceRef(cart);
        const session = this.readInvoiceSession();
        const body = this.toInvoiceApiBody(values);
        let saved = false;

        // Same tab + same draft: update in place (cart already holds the ref).
        if (session && cartRef && session.invoiceRef === cartRef) {
          try {
            await this.invoiceApi('PUT', `/${cartRef}`, { editToken: session.editToken, body });
            saved = true;
          } catch (error) {
            // Expired/locked/unknown draft: fall through to a full replacement.
            if (!(error instanceof InvoiceApiError) || ![404, 409].includes(error.status)) throw error;
          }
        }

        if (!saved) {
          const created = await this.invoiceApi('POST', '', {
            body: { ...body, cart_token: String(cart?.token || '') }
          });
          createdRef = String(created?.invoice_ref || '');
          createdToken = String(created?.edit_token || '');
          if (!INVOICE_REF_PATTERN.test(createdRef) || !EDIT_TOKEN_PATTERN.test(createdToken)) {
            throw new Error('INVALID_DRAFT_RESPONSE');
          }

          // Only now does Shopify learn about the draft — and only its ref.
          const updatedCart = await this.setCartInvoiceAttributes({
            invoice_type: 'corporate',
            invoice_ref: createdRef
          });
          if (this.cartInvoiceRef(updatedCart) !== createdRef) throw new Error('CART_REF_NOT_PERSISTED');

          this.writeInvoiceSession(createdRef, createdToken);
          const replaced = session && session.invoiceRef !== createdRef ? session : null;
          createdRef = null;
          createdToken = null;
          // Old draft is discarded only after the replacement is fully in place.
          if (replaced) await this.cancelInvoiceDraft(replaced.invoiceRef, replaced.editToken);
          this.render(updatedCart);
        }

        this.invoiceCorporateActive = true;
        this.updateCorporateButtonState();
        if (this.invoiceRemoveButton) this.invoiceRemoveButton.hidden = false;
        if (this.invoiceRecoverNote) this.invoiceRecoverNote.hidden = true;
        if (this.invoiceStatus) this.invoiceStatus.textContent = 'Fatura bilgileri kaydedildi.';
        this.closeInvoicePanel();
      } catch (error) {
        // Backend saved but Shopify did not: remove the orphan draft.
        if (createdRef && createdToken) await this.cancelInvoiceDraft(createdRef, createdToken);
        if (error instanceof InvoiceApiError && error.status === 400) this.showServerFieldErrors(error.fields);
        if (this.invoiceStatus) this.invoiceStatus.textContent = this.invoiceErrorMessage(error);
      } finally {
        this.invoiceBusy = false;
        if (this.invoiceSaveButton) this.invoiceSaveButton.disabled = false;
      }
    }

    // Clears the Shopify association first (so a failed backend call can never
    // leave the cart pointing at a cancelled draft), then cancels the draft if
    // this tab holds its edit token. Without a token the draft is never read;
    // it simply expires server-side.
    async removeInvoice() {
      if (this.invoiceBusy) return;
      this.invoiceBusy = true;
      if (this.invoiceRemoveButton) this.invoiceRemoveButton.disabled = true;
      if (this.invoiceStatus) this.invoiceStatus.textContent = 'Kaldırılıyor…';
      try {
        const session = this.readInvoiceSession();
        const cart = await this.setCartInvoiceAttributes({ invoice_type: '', invoice_ref: '' });
        const stillCorporate = cart?.attributes?.invoice_type === 'corporate' || Boolean(cart?.attributes?.invoice_ref);
        if (stillCorporate) throw new Error('CART_NOT_CLEARED');

        if (session) await this.cancelInvoiceDraft(session.invoiceRef, session.editToken);
        this.clearInvoiceSession();
        this.render(cart);

        this.invoiceCorporateActive = false;
        this.updateCorporateButtonState();
        this.resetInvoiceForm();
        if (this.invoiceRemoveButton) this.invoiceRemoveButton.hidden = true;
        if (this.invoiceRecoverNote) this.invoiceRecoverNote.hidden = true;
        if (this.invoiceStatus) this.invoiceStatus.textContent = 'Kurumsal fatura bilgileri kaldırıldı.';
        this.closeInvoicePanel();
      } catch (_) {
        if (this.invoiceStatus) this.invoiceStatus.textContent = 'Fatura bilgileri kaldırılamadı. Lütfen tekrar dene.';
      } finally {
        this.invoiceBusy = false;
        if (this.invoiceRemoveButton) this.invoiceRemoveButton.disabled = false;
      }
    }

    handleClick(event) {
      const relatedButton = event.target.closest?.('[data-wmc-related-add]');
      if (relatedButton) {
        event.preventDefault();
        this.addRelated(relatedButton);
        return;
      }
      const row = event.target.closest?.('[data-wmc-line]');
      if (!row) return;
      const key = row.dataset.key;
      const quantity = Number(row.querySelector('output')?.textContent) || 1;
      if (event.target.closest('[data-wmc-minus]')) this.change(key, Math.max(0, quantity - 1), row);
      else if (event.target.closest('[data-wmc-plus]')) this.change(key, quantity + 1, row);
      else if (event.target.closest('[data-wmc-remove]')) this.change(key, 0, row);
    }
  }

  if (!customElements.get('wemond-mini-cart')) customElements.define('wemond-mini-cart', WemondMiniCart);
  const getMiniCart = () => document.querySelector('wemond-mini-cart#wemond-mini-cart');
  window.WemondMiniCart = {
    open: (options) => getMiniCart()?.open(options),
    close: () => getMiniCart()?.close(),
    refresh: () => getMiniCart()?.refresh({ preserveScroll: true })
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-wemond-mini-cart-trigger]');
    if (!trigger) return;
    event.preventDefault();
    getMiniCart()?.open();
  });

  document.addEventListener(CART_EVENT, (event) => {
    const settle = () => {
      const miniCart = getMiniCart();
      if (!miniCart) return;
      const action = event.action || event.detail?.action;
      const run = () => {
        if (action === 'add') miniCart.open({ announceAdd: true });
        else if (miniCart.classList.contains('is-open')) miniCart.refresh({ preserveScroll: true }).catch(() => {});
      };
      const quickAdd = document.getElementById('quick-add-dialog');
      const quickAddDialog = quickAdd?.querySelector?.('dialog[open]') || null;

      if (action === 'add' && quickAddDialog?.open) {
        // The native Horizon quick-add normally closes itself when the cart update
        // promise resolves. Our custom mini cart also listens to that same promise,
        // so make the sequencing explicit: close the grind/quick-add window first,
        // then open the mini cart. This prevents the modal being left behind the
        // cart drawer when another listener misses or races the close event.
        let cartOpened = false;
        const openAfterClose = () => {
          if (cartOpened) return;
          cartOpened = true;
          run();
        };

        quickAddDialog.addEventListener('close', openAfterClose, { once: true });
        quickAdd?.addEventListener?.('dialog:close', openAfterClose, { once: true });

        try {
          if (typeof quickAdd?.closeDialog === 'function') quickAdd.closeDialog();
          else quickAddDialog.close();
        } catch (_) {
          try { quickAddDialog.close(); } catch (_) {}
        }

        // Safety net for a stale Chromium dialog animation. The shared dialog
        // component has its own timeout too, but this guarantees that the custom
        // cart flow can never strand the product window on screen.
        window.setTimeout(() => {
          if (quickAddDialog.open) {
            try { quickAddDialog.close(); } catch (_) {}
          }
          openAfterClose();
        }, 420);
      } else {
        run();
      }
    };
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then((result) => {
        const detail = result?.detail || result;
        if (!detail?.didError && !detail?.error) settle();
      }).catch(() => {});
    } else {
      window.setTimeout(settle, 120);
    }
  });
})();
